import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { FormWrapper } from '../components/forms/FormWrapper';
import { Card, Button, Input, Logo } from '../components/ui';
import toast from 'react-hot-toast';
import type { LoginRequest } from '../types';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';

// ===============================
// SCHEMA DE VALIDACIÓN
// ===============================

const loginSchema = z.object({
  username: z
    .string()
    .min(1, 'El usuario es requerido')
    .max(50, 'El usuario no puede tener más de 50 caracteres'),
  password: z
    .string()
    .min(1, 'La contraseña es requerida')
    .min(4, 'La contraseña debe tener al menos 4 caracteres')
});

type LoginFormData = z.infer<typeof loginSchema>;

// ===============================
// ICONOS
// ===============================

const UserIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const LockIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
  </svg>
);

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const LoginView: React.FC = () => {
  const location = useLocation();
  const { iniciarSesion, estaAutenticado, cargando, tieneAlgunPermiso } = useAuth();
  const { t } = useTranslation();

  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [enviando, setEnviando] = useState(false);

  // Verificar si viene de una sesión expirada
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const expired = urlParams.get('expired');
    
    if (expired === 'true') {
      toast.error(t('auth.session_expired'), {
        duration: 5000,
        id: 'session-expired-login'
      });
      
      // Limpiar el parámetro de la URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location]);

  // Redirigir si ya está autenticado
  if (estaAutenticado && !cargando) {
    // Respetar la página de origen si existe; si no, elegir el primer módulo accesible
    const from = (location.state as any)?.from?.pathname;
    let target = from && from !== '/' ? from : null;
    if (!target) {
      if (tieneAlgunPermiso(['dashboard.ver']))          target = '/dashboard';
      else if (tieneAlgunPermiso(['reportes.ver']))      target = '/reportes';
      else if (tieneAlgunPermiso(['analisis.ejecutar'])) target = '/analisis';
      else if (tieneAlgunPermiso(['gestion.ver']))       target = '/gestion';
      else if (tieneAlgunPermiso(['faenas.ver', 'maquinas.ver', 'operadores.ver', 'admin.ver'])) target = '/admin';
      else target = '/dashboard'; // último recurso; PrivateRoute ya tiene botón de logout
    }
    window.location.href = target;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">{t('auth.redirecting')}</p>
        </div>
      </div>
    );
  }

  // ===============================
  // MANEJADORES
  // ===============================

  const handleSubmit = async (data: LoginFormData) => {
    try {
      setEnviando(true);
      
      const loginData: LoginRequest = {
        username: data.username.trim(),
        password: data.password
      };

      await iniciarSesion(loginData);
      
      // Login exitoso
      
    } catch (error) {
      // El error ya se muestra via toast en el hook useAuth
    } finally {
      setEnviando(false);
    }
  };

  const toggleMostrarPassword = () => {
    setMostrarPassword(prev => !prev);
  };

  // ===============================
  // RENDER
  // ===============================

  return (
    <div className="min-h-screen bg-gradient-to-br from-mava-50 via-white to-mava-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl border-0" padding="lg">
          <div className="text-center mb-8">
            <Logo size="lg" showText showSubtext />
          </div>
          
          {/* Mensaje de sesión expirada */}
          {location.search.includes('expired=true') && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-orange-700">
                    {t('auth.session_expired_desc')}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <FormWrapper<LoginFormData>
            schema={loginSchema}
            onSubmit={handleSubmit}
            defaultValues={{
              username: '',
              password: ''
            }}
          >
            {({ register, formState: { errors, isValid }, watch }) => (
              <>
                {/* Campo Usuario */}
                <div className="space-y-2">
                  <Input
                    {...register('username')}
                    type="text"
                    label={t('auth.username')}
                    placeholder={t('auth.username_placeholder')}
                    error={errors.username?.message}
                    startIcon={<UserIcon />}
                    autoComplete="username"
                    autoFocus
                    disabled={enviando || cargando}
                  />
                </div>

                {/* Campo Contraseña */}
                <div className="space-y-2">
                  <Input
                    {...register('password')}
                    type={mostrarPassword ? 'text' : 'password'}
                    label={t('auth.password')}
                    placeholder={t('auth.password_placeholder')}
                    error={errors.password?.message}
                    startIcon={<LockIcon />}
                    endIcon={
                      <button
                        type="button"
                        onClick={toggleMostrarPassword}
                        className="hover:text-gray-600 transition-colors"
                        disabled={enviando || cargando}
                      >
                        {mostrarPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    }
                    autoComplete="current-password"
                    disabled={enviando || cargando}
                  />
                </div>

                {/* Botón de Envío */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={enviando || cargando}
                    disabled={!isValid || enviando || cargando}
                  >
                    {enviando ? t('auth.logging_in') : t('auth.login')}
                  </Button>
                </div>

                {/* Información adicional */}
                <div className="text-center text-sm text-gray-500 pt-4 border-t border-gray-100">
                  <p>{t('auth.login_trouble')}</p>
                  <p className="mt-1">{t('auth.contact_system_admin')}</p>
                </div>

                {/* Debug info - Solo en desarrollo */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="mt-6 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
                    <p><strong>Debug Info:</strong></p>
                    <p>Usuario: {watch('username')}</p>
                    <p>Form válido: {isValid ? 'Sí' : 'No'}</p>
                    <p>Enviando: {enviando ? 'Sí' : 'No'}</p>
                    <p>Cargando: {cargando ? 'Sí' : 'No'}</p>
                  </div>
                )}
              </>
            )}
          </FormWrapper>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>MAVA - Sur Data Consultores © 2024</p>
          <button
            onClick={() => i18n.changeLanguage(i18n.language === 'es' ? 'pt-BR' : 'es')}
            className="mt-3 text-lg hover:opacity-70 transition-opacity"
            title="Cambiar idioma / Mudar idioma"
          >
            {i18n.language === 'es' ? '🇧🇷' : '🇨🇱'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginView;